import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCurrency, formatNumber, getMonthName, getMonthNameShort, getCurrentYear } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { BarChart3, TrendingUp, Wallet, Receipt, Calculator, Clock, ShoppingBag, Star, Timer, Package, AlertTriangle, ShieldCheck, ShieldAlert, ThermometerSnowflake, Snowflake } from 'lucide-react';

const COLORS = ['#FF6B6B', '#00CCBC', '#06C167', '#F59E0B', '#8B5CF6', '#EC4899'];

export function DashboardKPIPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [caMensuel, setCaMensuel] = useState([]);
  const [repartition, setRepartition] = useState([]);
  const [tvaData, setTvaData] = useState([]);
  const [uberData, setUberData] = useState(null);
  const [conformite, setConformite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { annee: year };
      if (month) params.mois = month;

      const [kpiRes, caRes, repRes, tvaRes, uberRes, conformiteRes] = await Promise.all([
        api.get('/dashboard/kpis', { params }),
        api.get('/dashboard/ca-mensuel', { params: { annee: year } }),
        api.get('/dashboard/repartition-plateformes', { params }),
        api.get('/tva', { params: { annee: year } }),
        api.get('/dashboard/uber-verification', { params }).catch(() => ({ data: null })),
        api.get('/dashboard/conformite-jour').catch(() => ({ data: null })),
      ]);

      setKpis(kpiRes.data);
      setCaMensuel(caRes.data.map(d => ({
        ...d,
        moisLabel: getMonthNameShort(d.mois),
        ecart: d.ca_reel - d.ca_prevu
      })));
      setRepartition(repRes.data.filter(d => d.total > 0));
      setTvaData(tvaRes.data.map(d => ({
        ...d,
        moisLabel: getMonthNameShort(d.mois)
      })));
      setUberData(uberRes.data);
      setConformite(conformiteRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !kpis) {
    return (
      <div className="space-y-6">
        <div className="h-96 flex items-center justify-center">
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="module-header">
        <div>
          <h1 className="module-title flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            Dashboard KPI
          </h1>
          <p className="text-muted-foreground mt-1">Vue d'ensemble détaillée des performances</p>
        </div>
        <div className="flex gap-2">
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Tous les mois" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les mois</SelectItem>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Conformité HACCP du jour */}
      {conformite && (
        <Card
          className={`border-2 ${
            conformite.nb_done === conformite.nb_total
              ? 'border-green-300 bg-green-50/40'
              : conformite.nb_done === 0
                ? 'border-red-300 bg-red-50/40'
                : 'border-amber-300 bg-amber-50/40'
          }`}
          data-testid="conformite-jour-widget"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  conformite.nb_done === conformite.nb_total
                    ? 'bg-green-100 text-green-700'
                    : conformite.nb_done === 0
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                }`}>
                  {conformite.nb_done === conformite.nb_total
                    ? <ShieldCheck className="w-5 h-5" />
                    : <ShieldAlert className="w-5 h-5" />}
                </div>
                <div>
                  <p className="font-bold text-sm">Conformité HACCP du jour</p>
                  <p className="text-xs text-muted-foreground">{new Date(conformite.date).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })} · {conformite.nb_done}/{conformite.nb_total} fait</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {conformite.modules.map(m => {
                  const Icon = m.key === 'inventaire' ? Package : m.key === 'temperatures' ? ThermometerSnowflake : Snowflake;
                  const target = m.key === 'inventaire' ? '/inventaire' : m.key === 'temperatures' ? '/hygiene' : '/decongelation';
                  return (
                    <button
                      key={m.key}
                      onClick={() => navigate(target)}
                      className={`px-3 py-2 rounded-md border text-xs font-semibold flex items-center gap-2 transition hover:scale-105 ${
                        m.done
                          ? 'bg-green-100 border-green-300 text-green-800'
                          : 'bg-red-100 border-red-300 text-red-800'
                      }`}
                      data-testid={`conformite-${m.key}`}
                      title={m.done ? `${m.count} action(s) enregistrée(s) aujourd'hui` : "Cliquez pour aller faire la saisie"}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{m.label}</span>
                      <span className="ml-1 text-base">{m.done ? '🟢' : '🔴'}</span>
                      {m.done && <span className="text-[10px] opacity-70">({m.count})</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Wallet className="w-4 h-4" />
              <span className="text-xs">CA Réel</span>
            </div>
            <p className="text-xl font-bold tabular-nums text-blue-600">{formatCurrency(kpis.ca_journal || 0)}</p>
            <p className="text-xs mt-1 text-muted-foreground">Ventes directes</p>
          </CardContent>
        </Card>

        <Card className="stat-card border-amber-200 bg-amber-50/30 dark:bg-amber-950/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-2">
              <Wallet className="w-4 h-4" />
              <span className="text-xs font-medium">CA Total</span>
            </div>
            <p className="text-xl font-bold tabular-nums text-amber-600">{formatCurrency(kpis.ca_reel)}</p>
            <p className="text-xs mt-1 text-muted-foreground">{uberData?.net > 0 ? `Inclut Uber vérifié : ${formatCurrency(uberData.net)}` : 'Sur place + Plateformes'}</p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Réalisation</span>
            </div>
            <p className="text-xl font-bold tabular-nums">{kpis.pct_realisation.toFixed(1)}%</p>
            <p className="text-xs mt-1 text-muted-foreground">du CA prévu</p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Receipt className="w-4 h-4" />
              <span className="text-xs">Dépenses</span>
            </div>
            <p className="text-xl font-bold tabular-nums text-red-600">{formatCurrency(kpis.depenses_reelles)}</p>
            <p className="text-xs mt-1 text-muted-foreground">
              Budget: {formatCurrency(kpis.depenses_prevues)}
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Calculator className="w-4 h-4" />
              <span className="text-xs">TVA à payer</span>
            </div>
            <p className="text-xl font-bold tabular-nums text-amber-600">{formatCurrency(kpis.tva_a_payer)}</p>
            <p className="text-xs mt-1 text-muted-foreground">
              Collectée: {formatCurrency(kpis.tva_collectee)}
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Heures</span>
            </div>
            <p className="text-xl font-bold tabular-nums">{formatNumber(kpis.heures_reelles, 1)}h</p>
            <p className={`text-xs mt-1 ${kpis.ecart_heures > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {kpis.ecart_heures > 0 ? '+' : ''}{formatNumber(kpis.ecart_heures, 1)}h vs prévu
            </p>
          </CardContent>
        </Card>

        <Card className={`stat-card ${kpis.transactions_non_categorisees > 0 ? 'border-amber-300' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <span className="text-xs">Non catégorisées</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${kpis.transactions_non_categorisees > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {kpis.transactions_non_categorisees}
            </p>
            <p className="text-xs mt-1 text-muted-foreground">transactions</p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Uber Eats */}
      {uberData?.a_des_donnees && (
        <Card className="border-black bg-gradient-to-br from-zinc-50 to-stone-50 dark:from-zinc-900 dark:to-stone-900" data-testid="uber-performance-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-black text-white flex items-center justify-center font-bold text-xs">U</div>
                Performance Uber Eats {month ? `· ${getMonthName(month)}` : ''} {year}
              </CardTitle>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">vs déclaré (Plateformes) :</span>
                <span className={`font-semibold ${uberData.ecart_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {uberData.ecart_net >= 0 ? '+' : ''}{formatCurrency(uberData.ecart_net)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <UberKpi icon={ShoppingBag} label="Commandes" value={uberData.nb_commandes} color="text-zinc-700" />
              <UberKpi icon={Wallet} label="Brut TTC" value={formatCurrency(uberData.brut_ttc)} color="text-blue-600" />
              <UberKpi icon={Receipt} label={`Commission (${uberData.commission_pct}%)`} value={formatCurrency(Math.abs(uberData.commission_ttc))} color="text-red-600" />
              <UberKpi icon={TrendingUp} label="Net Uber" value={formatCurrency(uberData.net)} color="text-green-600" />
              <UberKpi icon={Package} label="Panier moyen" value={formatCurrency(uberData.panier_moyen)} color="text-amber-600" />
              <UberKpi icon={Star} label="Note moyenne" value={uberData.rating_count > 0 ? `${uberData.rating_avg} (${uberData.rating_count})` : '—'} color="text-yellow-500" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <UberKpi icon={Timer} label="Acceptation" value={`${uberData.delai_acceptation_min} min`} color="text-slate-600" small />
              <UberKpi icon={Timer} label="Préparation" value={`${uberData.temps_prep_min} min`} color="text-slate-600" small />
              <UberKpi icon={Timer} label="Livraison" value={`${uberData.temps_livraison_min} min`} color="text-slate-600" small />
              <UberKpi
                icon={AlertTriangle}
                label="Annulées"
                value={`${uberData.nb_annulees} / ${uberData.taux_completion}% OK`}
                color={uberData.nb_annulees > 0 ? 'text-red-600' : 'text-green-600'}
                small
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {uberData.by_hour.length > 0 && (
                <div className="bg-background/60 rounded-md p-3 border">
                  <p className="text-sm font-semibold mb-2">Commandes par heure</p>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={uberData.by_hour}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="heure" tickFormatter={h => `${h}h`} fontSize={10} />
                        <YAxis fontSize={10} />
                        <Tooltip formatter={(v, k) => k === 'brut' ? formatCurrency(v) : v} labelFormatter={h => `${h}h - ${Number(h) + 1}h`} />
                        <Bar dataKey="nb" fill="#000" name="Commandes" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {uberData.by_dow.length > 0 && (
                <div className="bg-background/60 rounded-md p-3 border">
                  <p className="text-sm font-semibold mb-2">CA par jour de la semaine</p>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={uberData.by_dow}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="jour" fontSize={10} />
                        <YAxis fontSize={10} tickFormatter={v => `${v}€`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="brut" fill="#06C167" name="Brut TTC" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {Math.abs(uberData.ecart_net) > 1 && (
              <div className={`rounded-md p-3 border text-xs flex items-start gap-2 ${uberData.ecart_net < 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-green-50 border-green-200 text-green-900'}`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Réconciliation Uber Eats</p>
                  <p>Vérifié (Linxo/import) : <b>{formatCurrency(uberData.net)}</b> · Déclaré (Plateformes) : <b>{formatCurrency(uberData.ca_declare_net)}</b> · Écart : <b>{uberData.ecart_net >= 0 ? '+' : ''}{formatCurrency(uberData.ecart_net)}</b></p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CA Evolution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Évolution CA {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={caMensuel}>
                  <defs>
                    <linearGradient id="colorPrevu" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorReel" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="moisLabel" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Area type="monotone" dataKey="ca_prevu" name="Prévu" stroke="hsl(var(--muted-foreground))" fillOpacity={1} fill="url(#colorPrevu)" />
                  <Area type="monotone" dataKey="ca_reel" name="Réel" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorReel)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Répartition Plateformes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Répartition par canal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {repartition.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={repartition}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="total"
                      nameKey="plateforme"
                    >
                      {repartition.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Aucune donnée
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Écart CA */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Écart CA par mois</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={caMensuel}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="moisLabel" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar
                    dataKey="ecart"
                    name="Écart"
                    radius={[4, 4, 0, 0]}
                  >
                    {caMensuel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.ecart >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* TVA Evolution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">TVA mensuelle</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tvaData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="moisLabel" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${v}€`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="tva_collectee" name="Collectée" stroke="#10B981" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="tva_deductible" name="Déductible" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="tva_a_payer" name="À payer" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Métriques de performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Marge brute estimée</p>
              <p className="text-2xl font-bold tabular-nums">
                {formatCurrency(kpis.ca_reel - kpis.depenses_reelles)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {kpis.ca_reel > 0 ? ((kpis.ca_reel - kpis.depenses_reelles) / kpis.ca_reel * 100).toFixed(1) : 0}% du CA
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Coût horaire moyen</p>
              <p className="text-2xl font-bold tabular-nums">
                {kpis.heures_reelles > 0 ? formatCurrency(kpis.depenses_reelles / kpis.heures_reelles) : '-'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">par heure travaillée</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">CA par heure</p>
              <p className="text-2xl font-bold tabular-nums">
                {kpis.heures_reelles > 0 ? formatCurrency(kpis.ca_reel / kpis.heures_reelles) : '-'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">productivité horaire</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Taux de dépenses</p>
              <p className="text-2xl font-bold tabular-nums">
                {kpis.ca_reel > 0 ? (kpis.depenses_reelles / kpis.ca_reel * 100).toFixed(1) : 0}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">dépenses / CA</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function UberKpi({ icon: Icon, label, value, color, small = false }) {
  return (
    <div className="bg-background/60 rounded-md p-3 border flex items-start gap-2">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground truncate uppercase tracking-wide">{label}</p>
        <p className={`${small ? 'text-sm' : 'text-base'} font-bold tabular-nums ${color}`}>{value}</p>
      </div>
    </div>
  );
}
